import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Statistics from "../components/Statistics";
import ResumeUpload from "../components/ResumeUpload";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Career = () => {
  const [heroData, setHeroData] = useState({
    title: "",
    subtitle: "",
    description: "",
    image: "",
  });

  const [opportunitiesData, setOpportunitiesData] = useState({
    title: "",
    subtitle: "",
    description: "",
    we_hire_for_text: "",
    we_hire_for_list: [],
  });

  const [contentLoading, setContentLoading] = useState(true);

  const benefits = [
    {
      title: "Work on Landmark Projects",
      description:
        "Be part of signature plotted and residential developments that shape South India's real estate landscape.",
      icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50",
    },
    {
      title: "Growth-Oriented Culture",
      description:
        "Access mentorship from industry leaders, clear role ownership, and structured learning to accelerate your career.",
      icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
      gradient: "from-green-500 to-emerald-500",
      bgGradient: "from-green-50 to-emerald-50",
    },
    {
      title: "Ethics & Transparency",
      description:
        "Work in an environment that values trust, compliance, and long-term relationships with customers and partners.",
      icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-50 to-pink-50",
    },
    {
      title: "Impact That Matters",
      description:
        "Contribute to projects that deliver real value to families, investors, and communities, not just vanity numbers.",
      icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
      gradient: "from-orange-500 to-red-500",
      bgGradient: "from-orange-50 to-red-50",
    },
  ];

  useEffect(() => {
    fetchCareerContent();
  }, []);

  const fetchCareerContent = async () => {
    setContentLoading(true);
    try {
      const heroResponse = await axios.get(`${API_BASE_URL}/career/hero`);
      if (heroResponse.data?.length > 0) {
        setHeroData(heroResponse.data[0]);
      }

      const oppResponse = await axios.get(`${API_BASE_URL}/career/opportunities`);
      if (oppResponse.data?.length > 0) {
        const data = oppResponse.data[0];
        const weHireForRaw = data.we_hire_for || "";
        const weHireForList = weHireForRaw
          .split(/\r?\n|,/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0);

        setOpportunitiesData({
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          we_hire_for_text: weHireForRaw,
          we_hire_for_list: weHireForList,
        });
      }
    } catch (error) {
      console.error("Error fetching career content:", error);
    } finally {
      setContentLoading(false);
    }
  };

  if (contentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO SECTION */}
      <section className="relative pt-24 sm:pt-28 md:pt-32 lg:pt-36 py-10 sm:py-14 md:py-20 px-4 sm:px-6 md:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 md:gap-16 items-center">
            <div className="space-y-5 sm:space-y-6 text-left">
              {heroData.subtitle && (
                <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 text-xs sm:text-sm font-semibold rounded-full">
                  {heroData.subtitle}
                </div>
              )}

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {heroData.title || "Join Our Team"}
              </h1>

              {heroData.description && (
                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed max-w-lg text-justify">
                  {heroData.description}
                </p>
              )}

              <a
                href="#application"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold sm:font-bold text-sm sm:text-lg hover:bg-blue-700 transition-all shadow-md sm:shadow-lg"
              >
                Share Your Resume
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
            </div>

            <div className="relative">
              <img
                src={
                  heroData.image ||
                  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80"
                }
                alt="Team collaboration"
                className="w-full h-56 sm:h-72 md:h-[480px] object-cover rounded-2xl shadow-xl sm:shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <Statistics />

      {/* OPPORTUNITIES + FORM */}
      <section
        id="application"
        className="pt-16 sm:pt-20 md:pt-24 py-10 sm:py-14 md:py-16 px-4 sm:px-6 bg-white"
      >
        <div className="container mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 md:gap-16">
          {/* TEXT BLOCK */}
          <div className="text-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">
              {opportunitiesData.title || "Career Opportunities"}
            </h2>

            {opportunitiesData.subtitle && (
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-3 sm:mb-4">
                {opportunitiesData.subtitle}
              </h3>
            )}

            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed text-justify">
              {opportunitiesData.description ||
                "HPR Infra is a South India-based developer delivering HMDA-approved plotted and apartment projects."}
            </p>

            {opportunitiesData.we_hire_for_list?.length > 0 && (
              <div className="mt-6 sm:mt-8">
                <h4 className="text-lg font-bold text-gray-800 mb-3 sm:mb-4">
                  We Hire For:
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {opportunitiesData.we_hire_for_list.map((role, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100 hover:border-blue-300 transition-all"
                    >
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm sm:text-base text-gray-700 font-medium">
                        {role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* FORM */}
          <ResumeUpload />
        </div>
      </section>

      {/* BENEFITS */}
      <section className="pt-20 sm:pt-24 md:pt-28 pb-16 px-4 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 text-sm font-semibold rounded-full mb-4">
              Why Choose Us
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Work With Us
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Join a team that values quality, transparency, and professional growth in every aspect of our work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent hover:-translate-y-2"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${benefit.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`}
                ></div>

                <div className="relative z-10">
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${benefit.gradient} rounded-xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 shadow-lg`}
                  >
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={benefit.icon} />
                    </svg>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                    {benefit.title}
                  </h3>

                  <p className="text-gray-600 text-sm text-center leading-relaxed">
                    {benefit.description}
                  </p>
                </div>

                <div
                  className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${benefit.gradient} opacity-10 rounded-bl-full rounded-tr-2xl`}
                ></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Career;
